import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Clock3, Download, Info, X } from 'lucide-react';
import type { AlertEvidenceView, EvidenceCoverage } from '../../types/alert';
import { CAMERA_LABEL_LABELS } from '../../utils/constants';
import { ClipDownloadError } from '../../utils/evidenceDownload';
import { describeGaps } from '../../utils/recordingGaps';
import { downloadEvidenceClip, saveBlobAsFile, ClipHttpError } from '../../lib/api/evidenceClip';
import { useHlsPlayer } from '../../hooks/useHlsPlayer';
import { Button } from '../ui/Button';
import { Select } from '../ui/Input';
import { Spinner } from '../ui/Spinner';

// Los tres primeros son ESTADOS DEL MUNDO, no fallos (ADR-025): se explican en
// un aviso neutro, nunca en rojo. `NO_FOOTAGE` es hoy lo que devuelven casi
// todos los SOS, porque el equipo lleva días apagado.
const COVERAGE_TEXT: Record<EvidenceCoverage, string | null> = {
  NO_RECORDER: 'Este vehículo no tiene grabador de video, así que esta alerta no tiene video.',
  OUT_OF_RETENTION: 'El video de esa fecha ya no está disponible: el archivo del grabador guarda 30 días.',
  NO_FOOTAGE: 'El vehículo tenía grabador pero no grabó en ese momento (equipo apagado o sin señal).',
  PARTIAL: 'Hay tramos sin grabación dentro de la ventana. Aparecen en amarillo en la línea de tiempo.',
  COMPLETE: null,
};

const CLIP_ROLE_LABEL = { WINDOW: 'Ventana completa', OPENING: 'Inicio del incidente', OUTCOME: 'Desenlace' } as const;

function formatInstant(instant: string | null): string {
  if (!instant) return '—';
  return new Intl.DateTimeFormat('es-CO', { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(instant));
}

function percentWithin(from: string, to: string, instant: string): number {
  const start = new Date(from).getTime();
  const total = new Date(to).getTime() - start;
  return total <= 0 ? 0 : ((new Date(instant).getTime() - start) / total) * 100;
}

function fileSafe(text: string): string {
  // NFD separa "á" en "a" + acento suelto; `\p{M}` son las marcas combinantes.
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function describeDownloadError(err: unknown): string {
  if (err instanceof ClipDownloadError) return err.message;
  if (err instanceof ClipHttpError) {
    if (err.status === 403) return 'No tienes permiso para descargar esta evidencia, o el enlace del video venció. Vuelve a abrir la alerta e inténtalo de nuevo.';
    return `No se pudo descargar el video (${err.message}).`;
  }
  if (err instanceof TypeError) return 'No se pudo descargar el video: falló la conexión.';
  return err instanceof Error ? err.message : 'No se pudo descargar el video.';
}

type DownloadState =
  | { status: 'idle' }
  | { status: 'working'; label: string }
  | { status: 'done'; converted: boolean; extension: string }
  | { status: 'error'; message: string };

interface AlertEvidenceViewerProps {
  evidence: AlertEvidenceView;
  /** Solo Supervisor/Ejecutivo/Administrador (`PRODUCT.md` §4.3/§4.8): el Operador ve el video pero no lo descarga. */
  canDownload: boolean;
  /** Prefijo del nombre del archivo descargado, p. ej. `evidencia-pat001`. */
  fileStem: string;
}

// FE-20 (rebanada "ver el video"): el bloque de video de una alerta. Una cámara
// a la vez, con selector — la grilla sincronizada es `FE-22`. Reusa el player
// HLS y las clases de la línea de tiempo del histórico (`FE-18`): evidencia e
// histórico son los mismos bytes.
export function AlertEvidenceViewer({ evidence, canDownload, fileStem }: AlertEvidenceViewerProps) {
  const cameras = evidence.cameras;
  const [cameraId, setCameraId] = useState(() => (cameras.find(c => c.clips.length > 0) ?? cameras[0])?.cameraId ?? '');
  const [clipIndex, setClipIndex] = useState(0);
  const [selectedInstant, setSelectedInstant] = useState<string | null>(null);
  const [download, setDownload] = useState<DownloadState>({ status: 'idle' });
  const abortRef = useRef<AbortController | null>(null);

  const camera = cameras.find(c => c.cameraId === cameraId) ?? cameras[0];
  const clip = camera?.clips[Math.min(clipIndex, Math.max(0, (camera?.clips.length ?? 1) - 1))];
  const win = evidence.window;

  const { videoRef, error: playerError, isReconnecting, isSupported, playingProgramDateTime, seekToProgramDateTime } = useHlsPlayer(
    clip?.playlistUrl ?? null,
    { seekToProgramDateTime: selectedInstant },
  );

  // Cerrar la alerta a media descarga no debe dejar segmentos bajando en segundo plano.
  useEffect(() => () => abortRef.current?.abort(), []);

  const cameraOptions = useMemo(() => cameras.map(c => ({
    value: c.cameraId,
    label: `${c.name || CAMERA_LABEL_LABELS[c.label]}${c.clips.length === 0 ? ' (sin video)' : ''}`,
  })), [cameras]);

  const jumpTo = (instant: string) => {
    setSelectedInstant(instant);
    // Volver a pulsar el mismo instante no cambia el estado; sin esta llamada
    // el segundo clic sobre un tramo no hace nada (medido en el histórico, FE-18).
    seekToProgramDateTime(instant);
  };
  const jumpPastGap = (gapEnd: string) => {
    const next = camera?.spans.find(span => new Date(span.from) >= new Date(gapEnd));
    if (next) jumpTo(next.from);
  };

  const startDownload = async () => {
    if (!clip || !camera) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setDownload({ status: 'working', label: 'Preparando…' });
    try {
      const result = await downloadEvidenceClip(clip.playlistUrl, {
        signal: controller.signal,
        onProgress: p => setDownload({
          status: 'working',
          label: p.phase === 'downloading' ? `Descargando ${p.done}/${p.total}…`
            : p.phase === 'converting' ? 'Convirtiendo a MP4…' : 'Preparando…',
        }),
      });
      const stamp = clip.from.replace(/[:.]/g, '-').slice(0, 19);
      saveBlobAsFile(result.blob, `${fileSafe(fileStem)}-${fileSafe(CAMERA_LABEL_LABELS[camera.label])}-${stamp}.${result.extension}`);
      setDownload({ status: 'done', converted: result.converted, extension: result.extension });
    } catch (err) {
      if (err instanceof ClipDownloadError && err.code === 'ABORTED') {
        setDownload({ status: 'idle' });
      } else {
        setDownload({ status: 'error', message: describeDownloadError(err) });
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const hasVideo = cameras.some(c => c.clips.length > 0);
  const coverageText = COVERAGE_TEXT[evidence.coverage];

  if (!hasVideo) {
    return (
      <div className="historical-video">
        <p className="historical-video__empty" style={{ padding: '0.75rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-lg)' }}>
          <Info size={14} style={{ verticalAlign: '-2px', marginRight: '0.35rem' }} />
          {coverageText ?? 'No hay video para esta alerta.'}
        </p>
      </div>
    );
  }

  return (
    <div className="historical-video">
      <p className="historical-video__legend" style={{ justifyContent: 'flex-start' }}>
        {/* `open` ya no implica que el video llegue hasta ahora: con el tope
            (#226) una alerta abierta deja de crecer a los 30 min, y decir
            "ahora" sería falso. `capped` manda sobre `open`. */}
        <Clock3 size={13} /> Ventana: {formatInstant(win.from)} → {win.open && !win.capped ? 'ahora (alerta abierta)' : formatInstant(win.to)}
        {' · '}{Math.round(win.preSeconds / 60)} min antes / {Math.round(win.postSeconds / 60)} min después
        {win.adjusted && ' · ajustada'}
      </p>

      {win.capped && (
        <p className="historical-video__empty" style={{ padding: '0.5rem 0.75rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-md)' }}>
          <Info size={13} style={{ verticalAlign: '-2px', marginRight: '0.35rem' }} />
          Video limitado a {Math.round(win.maxSeconds / 60)} min desde el inicio
          {win.open ? ', y la alerta sigue abierta' : ' porque se cerró más tarde'}: lo que pasó después
          no está en este clip. El archivo completo sigue en Histórico.
        </p>
      )}

      {coverageText && (
        <p className="historical-video__empty" style={{ padding: '0.5rem 0.75rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-md)' }}>
          <Info size={13} style={{ verticalAlign: '-2px', marginRight: '0.35rem' }} />{coverageText}
        </p>
      )}
      {evidence.truncated && (
        <p className="historical-video__empty">
          Alerta muy larga: se muestran solo el inicio y el desenlace. El tramo intermedio está en el histórico del vehículo.
        </p>
      )}

      {(cameras.length > 1 || (camera?.clips.length ?? 0) > 1) && (
        <div className="historical-video__controls">
          {cameras.length > 1 && (
            <Select
              label="Cámara"
              value={camera?.cameraId ?? ''}
              onChange={event => { setCameraId(event.target.value); setClipIndex(0); setSelectedInstant(null); setDownload({ status: 'idle' }); }}
              options={cameraOptions}
            />
          )}
          {(camera?.clips.length ?? 0) > 1 && (
            <Select
              label="Tramo"
              value={String(clipIndex)}
              onChange={event => { setClipIndex(Number(event.target.value)); setSelectedInstant(null); setDownload({ status: 'idle' }); }}
              options={camera!.clips.map((c, i) => ({ value: String(i), label: CLIP_ROLE_LABEL[c.role] }))}
            />
          )}
        </div>
      )}

      {camera && camera.clips.length === 0 ? (
        <p className="historical-video__empty" style={{ padding: '0.5rem 0.75rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-md)' }}>
          <Info size={13} style={{ verticalAlign: '-2px', marginRight: '0.35rem' }} />
          {COVERAGE_TEXT[camera.coverage] ?? 'Esta cámara no tiene video en la ventana.'}
        </p>
      ) : (
        <>
          <div className="historical-video__timeline" aria-label="Línea de tiempo de la grabación">
            {camera?.spans.map(span => {
              const left = percentWithin(win.from, win.to, span.from);
              const right = percentWithin(win.from, win.to, span.to);
              return (
                <button
                  key={`span-${span.from}`} type="button" className="historical-video__span"
                  style={{ left: `${left}%`, width: `${Math.max(0.4, right - left)}%` }}
                  title={`Grabación: ${formatInstant(span.from)} a ${formatInstant(span.to)}`}
                  onClick={() => jumpTo(span.from)}
                />
              );
            })}
            {camera?.gaps.map(gap => {
              const left = percentWithin(win.from, win.to, gap.from);
              const right = percentWithin(win.from, win.to, gap.to);
              return (
                <button
                  key={`gap-${gap.from}`} type="button" className="historical-video__gap"
                  style={{ left: `${left}%`, width: `${right - left}%` }}
                  title={`Sin grabación: ${formatInstant(gap.from)} a ${formatInstant(gap.to)}`}
                  onClick={() => jumpPastGap(gap.to)}
                >
                  {right - left > 12 ? 'Sin grabación' : ''}
                </button>
              );
            })}
          </div>
          <div className="historical-video__legend">
            <span><i className="historical-video__legend-recording" /> Grabación</span>
            <span><i className="historical-video__legend-gap" /> Sin grabación</span>
            {camera && describeGaps(camera.gaps) && <span className="historical-video__legend-summary">· {describeGaps(camera.gaps)}</span>}
          </div>

          <div className="historical-video__player">
            <video ref={videoRef} controls playsInline style={{ display: clip && isSupported && !playerError ? 'block' : 'none' }} />
            {!isSupported && clip && (
              <p className="historical-video__error"><AlertTriangle size={15} /> Este navegador no puede reproducir HLS autenticado.</p>
            )}
            {playerError && <p className="historical-video__error"><AlertTriangle size={15} /> {playerError}</p>}
            {isReconnecting && !playerError && (
              <p className="historical-video__reconnecting" role="status"><Spinner size="sm" /> Reconectando con el archivo…</p>
            )}
          </div>
          <p className="historical-video__played-at"><Clock3 size={13} /> Hora del fragmento: {formatInstant(playingProgramDateTime)}</p>

          {canDownload && clip && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                {download.status === 'working' ? (
                  <>
                    <Spinner size="sm" />
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{download.label}</span>
                    <Button size="sm" variant="ghost" leftIcon={<X size={13} />} onClick={() => abortRef.current?.abort()}>Cancelar</Button>
                  </>
                ) : (
                  <Button size="sm" variant="secondary" leftIcon={<Download size={14} />} onClick={() => void startDownload()}>
                    Descargar video
                  </Button>
                )}
              </div>
              <p className="historical-video__empty" style={{ fontSize: '11px' }}>
                El archivo se arma en tu navegador: un clip de 10 min puede pesar entre 150 y 300 MB.
              </p>
              {download.status === 'done' && !download.converted && (
                <p className="historical-video__empty">
                  No se pudo convertir este video a MP4, así que se descargó como <strong>.{download.extension}</strong> (se abre con VLC).
                </p>
              )}
              {download.status === 'error' && (
                <p className="historical-video__error"><AlertTriangle size={15} /> {download.message}</p>
              )}
            </div>
          )}
        </>
      )}

      {evidence.items.length > 0 && (
        <p className="historical-video__empty">
          Evidencia conservada: {evidence.items.length} clip{evidence.items.length === 1 ? '' : 's'}
          {evidence.items.some(item => item.retentionHold) && ' · retención indefinida'}.
        </p>
      )}
    </div>
  );
}
